<template>
    <BaseEnergyProductCard ref="baseCard" :product="product">

        <EnergyProductLogo class="element" :product="product" />

        <div class="product-price-container element">
            <Tooltip ref="priceSummaryTooltip" position="left">
                <template #toggle>
                    <EnergyProductPriceNoDiscount v-if="comparisonStore.filters.sort === 'monthly_installment'"
                        :product="product" class="clickable" />
                    <EnergyProductPriceMonthYear v-else :product="product" class="clickable" />
                </template>
                <template #text>
                    <EnergyProductPriceSummary @showPricing="showProductInfo()" :product="product" />
                </template>
            </Tooltip>

            <EnergyProductCta class="product-clickout-desktop" :product="product"></EnergyProductCta>
        </div>

        <hr class="product-divider horizontal element">
        <div class="product-discount element" v-if="product.total_discount > 0">
            <span v-if="product.total_discount > 0" class="discount-label clickable" @click="showProductInfo()">
                {{ toCurrencyShortEur(product.total_discount) }}
                korting
            </span>
        </div>

        <EnergyProductUspList class="element" :product="product" @showProductInfo="showProductInfo">
        </EnergyProductUspList>

        <div class="product-information-toggle-mobile element">
            <button class="clean-button element" @click="toggleInfo">
                <Icon size="sm" :icon="infoExpanded ? 'x-fa-minus-circle' : 'x-fa-plus-circle'" />
                <span>{{ infoExpanded ? 'Minder info' : 'Info & tarieven' }}</span>
            </button>
        </div>

        <EnergyProductCta class="product-clickout-mobile element" :product="product"></EnergyProductCta>

        <template #information ref="productInformation">
            <EnergyProductInfo class="element" v-if="infoExpanded" :product="product" :startTab="startTab">
            </EnergyProductInfo>
            <button @click="toggleInfo" class="clean-button product-information-toggle desktop element"
                :class="{ expanded: infoExpanded }">
                <Icon size="sm" :icon="infoExpanded ? 'x-fa-minus-circle' : 'x-fa-plus-circle'" />
                <span> {{ infoExpanded ? 'Minder informatie' : 'Meer info & tarieven' }}</span>
            </button>
        </template>
    </BaseEnergyProductCard>
</template>

<script setup>
import { ref, watch } from 'vue';
import EnergyProductCta from '../product/EnergyProductCta.vue';
import BaseEnergyProductCard from './BaseEnergyProductCard.vue';
import EnergyProductLogo from '../product/EnergyProductLogo.vue';
import Icon from '../../../../vue-elements/icon/Icon.vue';
import EnergyProductInfo from '../product/EnergyProductInfo.vue';
import EnergyProductUspList from '../product/EnergyProductUspList.vue';
import EnergyProductPriceMonthYear from '../product/EnergyProductPriceMonthYear.vue';
import Tooltip from '../../../../../abstracts/elements/tooltip/tooltip.vue';
import EnergyProductPriceSummary from '../product/EnergyProductPriceSummary.vue';
import { useEnergyComparisonStore } from '../../stores/energyComparison';
import EnergyProductPriceNoDiscount from '../product/EnergyProductPriceNoDiscount.vue';


const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
});

const priceSummaryTooltip = ref(null);
const infoExpanded = ref(false);
const startTab = ref('pricing');

const toggleInfo = () => {
    infoExpanded.value = !infoExpanded.value;
};

// Show extra info and close tooltip
const showProductInfo = (tab = 'pricing') => {
    infoExpanded.value = true;
    startTab.value = tab;
    if (priceSummaryTooltip.value) {
        priceSummaryTooltip.value.manualClose();
    }
};

// Ref of the basecard to interact with the element
const baseCard = ref(null);

// If the info is expanded, scroll the base card into view
watch(infoExpanded, (newValue) => {
    if (newValue) {
        baseCard.value.$el.scrollIntoView({ behavior: 'smooth' });
    }
});

const comparisonStore = useEnergyComparisonStore();

</script>

<style lang="scss">
@use 'assets/scss/all' as *;

.product-card.base {

    a:focus-visible,
    button:focus-visible {
        outline: 1px solid $all-blue-20;
    }

    .card-content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        row-gap: 16px;
        grid-template-areas: 'logo price' 'divider divider' 'discount discount' 'usp usp' 'info cta';
        column-gap: 12px;

        @include breakpoint-up(sm) {
            column-gap: 0;
            grid-template-columns: auto 1fr auto;
            grid-template-areas:
                'logo discount price'
                'divider divider price'
                'usp usp price'
                'usp usp price';
        }
    }

    .provider-logo-container {
        grid-area: logo;
        display: flex;
        width: fit-content;

    }

    .product-divider {
        grid-area: divider;
        margin: 0;
        color: $border-base;
        opacity: 1;
    }

    .product-discount {
        grid-area: discount;
        display: inline-flex;
        align-items: center;
    }

    .discount-label {
        display: inline-flex;

        color: $green-50;
        background: $green-20;
        border: 1px solid $green-50;;

        border-radius: 10px;
        font-weight: 600;
        padding: 8px 12px;

        @include breakpoint-up(sm) {
            padding: 12px;
            margin: 0 24px;
        }
    }

    .product-price-container {
        grid-area: price;
    }

    .clickable {
        cursor: pointer;
    }

    .energy-product-usp-list {
        grid-area: usp;
    }

    .product-clickout-desktop {
        display: none;
        width: 100%;

        @include breakpoint-up(sm) {
            display: flex;
            grid-area: cta;
        }
    }

    .product-clickout-mobile {
        display: flex;
        grid-area: cta;
        width: fit-content;
        justify-self: flex-end;
        width: 100%;

        @include breakpoint-up(sm) {
            display: none;
        }
    }

    .usps {
        grid-area: usp;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .product-price-container {
        display: flex;
        flex-direction: column;
        gap: 16px;
        min-width: 180px;
        align-items: flex-end;
        position: relative;

        @include breakpoint-up(sm) {
            padding-left: 16px;
            border-left: 1px solid $border-base;
        }

        .tooltip.bottom-left .tooltip-content-container,
        .tooltip.top-left .tooltip-content-container {
            right: 16px !important;
            --offset: 0px !important;
        }
    }

    .product-information-toggle-mobile {
        grid-area: info;
        display: flex;
        align-items: center;

        @include breakpoint-up(sm) {
            display: none;
        }
    }

    .product-information-toggle {

        @include flex-row-center;
        width: 100%;
        border-bottom-left-radius: inherit;
        border-bottom-right-radius: inherit;
        border-top: 1px solid $border-base;
        padding: 10px;
        font-weight: 600;

        @include breakpoint-down(sm) {
            display: none;
        }
    }
}
</style>
